/**
 * SHELL — reusable UI components shared across screens.
 */

import { div, el } from './dom';
import { icons } from './icons';
import type { IconName } from './icons';

/** App brand lockup (logo + wordmark). */
export function brand(subtitle?: string): HTMLElement {
  const logo = el('span', { class: 'dr-logo', html: icons.marble, ariaLabel: 'DailyRoll' });
  const node = div('dr-brand', logo, el('h1', { class: 'dr-title', text: 'DailyRoll' }));
  if (subtitle) {
    const wrap = div('');
    wrap.append(node, el('p', { class: 'dr-subtitle', text: subtitle }));
    return wrap;
  }
  return node;
}

/** Streak badge — the loss-aversion centerpiece. */
export function streakBadge(current: number, opts: { atRisk?: boolean; best?: number } = {}): HTMLElement {
  const node = div(opts.atRisk ? 'dr-streak dr-streak--risk' : 'dr-streak');
  node.setAttribute('role', 'status');
  const flame = el('span', { class: 'dr-streak__flame', html: icons.flame, ariaLabel: 'streak' });
  const col = div('');
  col.append(
    el('div', { class: 'dr-streak__num', text: String(current) }),
    el('div', { class: 'dr-streak__label', text: current === 1 ? 'day streak' : 'day streak' }),
  );
  node.append(flame, col);
  if (opts.atRisk) {
    node.append(el('div', { class: 'dr-streak__warn', text: 'At risk!' }));
  } else if (opts.best && opts.best > current) {
    node.append(el('div', { class: 'dr-streak__label', text: `best ${opts.best}` }));
  }
  return node;
}

export type BannerKind = 'info' | 'warn' | 'err';

export function banner(kind: BannerKind, message: string): HTMLElement {
  const iconName: IconName = kind === 'err' ? 'alert' : kind === 'warn' ? 'alert' : 'check';
  return div(
    `dr-banner dr-banner--${kind}`,
    el('span', { html: icons[iconName], ariaLabel: '' }),
    el('span', { text: message }),
  );
}

/** Centered spinner with an optional label. */
export function spinner(label?: string): HTMLElement {
  const node = div('dr-center');
  node.append(div('dr-spinner'));
  if (label) node.append(el('div', { class: 'dr-muted', text: label }));
  return node;
}

/** A 2-up metric tile grid. */
export function metricGrid(metrics: ReadonlyArray<{ label: string; value: string }>): HTMLElement {
  const grid = div('dr-metrics');
  for (const m of metrics) {
    grid.append(
      div('dr-metric', el('div', { class: 'dr-metric__val', text: m.value }), el('div', { class: 'dr-metric__label', text: m.label })),
    );
  }
  return grid;
}

/** A labelled button with a leading icon. */
export function iconButton(
  cls: string,
  iconName: IconName,
  label: string,
  onClick: () => void,
): HTMLButtonElement {
  return el(
    'button',
    { class: cls, type: 'button', on: { click: () => onClick() } },
    el('span', { html: icons[iconName], ariaLabel: '' }),
    el('span', { text: label }),
  );
}
